// Season presentation (year-in-review): a full-screen story the group can watch together at the end of the season,
// built from the group's photo feed, or from your own catches. Tap right/left to skip, hold to pause.
import type { Group, Kind } from "../shared/model.ts";
import type { TextKey } from "../shared/strings.ts";
import { $, el, toast } from "./dom.ts";
import { LOCALE, dFmtY, fmtNum, t } from "./i18n.ts";
import { photoUrl } from "./photos.ts";
import { cancelButton, closeSheet, grow, openSheet } from "./sheet.ts";
import { speciesName } from "./species.ts";
import { catches, onRender } from "./state.ts";
import { chatGroup } from "./groups/chat.ts";
import { foldSocial, isPost } from "./groups/feed.ts";
import { groupMessages, me } from "./groups/store.ts";
import { hasPlus } from "./billing.ts";
import { openPaywall } from "./paywall.ts";

const SLIDE_MS = 5500, MONTAGE_MS = 1700;

/** One post or catch in the season. */
interface Item {
  who: string;
  ts: number;
  image: string | null;
  isCatch: boolean;
  kind?: Kind;
  species?: string;
  weightKg?: number | null;
  lengthCm?: number | null;
  text: string;
  likes: number;
  topComment: { text: string; name: string } | null;
}

async function groupSeasonItems(g: Group): Promise<Item[]> {
  const msgs = await groupMessages(g.id), social = foldSocial(msgs);
  return msgs.filter(isPost).map((m) => {
    const cs = social.comments(m.id), c = m.type === "catch" ? m.catch : null;
    return { who: m.name, ts: c?.time || m.ts, image: m.image || null, isCatch: c != null,
      kind: c?.kind, species: c?.species, weightKg: c?.weightKg, lengthCm: c?.lengthCm, text: m.type === "photo" ? m.text : "",
      likes: social.likers(m.id).length, topComment: cs.length ? cs[cs.length - 1] : null };
  });
}
async function ownSeasonItems(): Promise<Item[]> {
  const out: Item[] = [];
  for (const c of catches) {
    const image = c.photoIds?.[0] ? await photoUrl(c.photoIds[0], "full") : null;
    out.push({ who: me.name || t("msgYou"), ts: c.time, image, isCatch: true, kind: c.kind, species: speciesName(c),
      weightKg: c.weightKg, lengthCm: c.lengthCm, text: c.place || "", likes: 0, topComment: null });
  }
  return out;
}
/** "Last 12 months" plus every year that has items, newest first. */
function seasonsIn(items: Item[]): { id: string; label: string }[] {
  const years = [...new Set(items.map((i) => new Date(i.ts).getFullYear()))].sort((a, b) => b - a);
  return [{ id: "12m", label: t("seasonLast12") }, ...years.map((y) => ({ id: String(y), label: String(y) }))];
}
function inSeason(items: Item[], id: string): Item[] {
  if (id === "12m") return items.filter((i) => i.ts >= Date.now() - 365 * 864e5);
  return items.filter((i) => new Date(i.ts).getFullYear() === Number(id));
}

/* ---------- slides ---------- */
type Slide = { draw: () => HTMLElement } | { montage: Item[] };
const sum = (a: number[]) => a.reduce((s, x) => s + x, 0);
function bars(rows: { label: string; value: number; note?: string }[], max: number): HTMLElement {
  return el("div", { class: "sbars" }, ...rows.map((r, i) => el("div", { class: "sbar", style: `--w:${Math.max(4, Math.round(r.value / max * 100))}%;--d:${i * 120}ms` },
    el("span", { class: "sbl", text: r.label }), el("span", { class: "sbf" }), el("b", { class: "num", text: r.note ?? String(r.value) }))));
}
const collage = (photos: Item[]) => el("div", { class: "collage" }, ...photos.map((p, i) => el("img", { src: p.image ?? "", alt: "", style: `--d:${i * 150}ms` })));
const byWeight = (a: Item, b: Item) => (b.weightKg ?? 0) - (a.weightKg ?? 0);

function buildSlides(title: string, items: Item[]): Slide[] {
  const slides: Slide[] = [], photos = items.filter((i) => i.image), fish = items.filter((i) => i.isCatch);
  const year = items.length ? new Date(Math.max(...items.map((i) => i.ts))).getFullYear() : new Date().getFullYear();
  const people = [...new Set(items.map((i) => i.who))];

  // 1. Opener with a collage
  slides.push({ draw: () => el("div", { class: "slide opener" }, collage(photos.slice(0, 6)),
    el("div", { class: "stext" }, el("small", { text: t("seasonKicker") }), el("h1", { text: title }), el("p", { class: "num", text: t("seasonOf", year) }))) });

  // 2. The season in numbers (fish weight only: a moose would swamp it)
  const weight = sum(fish.filter((i) => i.kind !== "game").map((i) => i.weightKg ?? 0)), species = new Set(fish.map((i) => i.species).filter(Boolean));
  const big = (n: string, label: string) => el("div", {}, el("b", { class: "num", text: n }), el("span", { text: label }));
  slides.push({ draw: () => el("div", { class: "slide numbers" }, el("h2", { text: t("seasonNumbers") }),
    el("div", { class: "bignums" },
      big(String(fish.length), t("seasonCatches")),
      big(String(species.size), t("seasonSpecies")),
      weight ? big(fmtNum(weight, 0) + " kg", t("seasonWeight")) : null,
      big(String(photos.length), t("seasonPhotos")),
      people.length > 1 ? big(String(people.length), t("seasonPeople")) : null)) });

  // 3. Biggest fish and biggest game, a slide each
  for (const [kind, label] of [["fish", "seasonBiggest"], ["game", "seasonBiggestGame"]] as [Kind, TextKey][]) {
    const biggest = fish.filter((i) => (i.kind ?? "fish") === kind && i.weightKg).sort(byWeight)[0];
    if (biggest) slides.push({ draw: () => el("div", { class: "slide hero" }, biggest.image ? el("img", { class: "heroimg", src: biggest.image, alt: "" }) : null,
      el("div", { class: "stext bottom" }, el("small", { text: t(label) }), el("h1", { class: "num", text: `${biggest.species ?? ""} ${fmtNum(biggest.weightKg, kind === "game" ? 0 : 2)} kg` }),
        el("p", { class: "num", text: [biggest.who, dFmtY.format(biggest.ts), biggest.lengthCm ? fmtNum(biggest.lengthCm) + " cm" : ""].filter(Boolean).join(" · ") }))) });
  }

  // 4. Species
  const bySpecies = new Map<string, number>();
  for (const i of fish) if (i.species) bySpecies.set(i.species, (bySpecies.get(i.species) ?? 0) + 1);
  const topSpecies = [...bySpecies].sort((a, b) => b[1] - a[1]).slice(0, 6);
  if (topSpecies.length > 1) slides.push({ draw: () => el("div", { class: "slide chart" }, el("h2", { text: t("seasonTopSpecies") }),
    bars(topSpecies.map(([label, value]) => ({ label, value })), topSpecies[0][1])) });

  // 5. Most liked photo
  const liked = photos.filter((i) => i.likes).sort((a, b) => b.likes - a.likes)[0];
  if (liked) slides.push({ draw: () => el("div", { class: "slide hero" }, el("img", { class: "heroimg", src: liked.image ?? "", alt: "" }),
    el("div", { class: "stext bottom" }, el("small", { text: t("seasonMostLiked") }), el("h1", { text: t("seasonLikes", liked.likes) }),
      el("p", { text: liked.who + (liked.text ? " · " + liked.text : "") }),
      liked.topComment ? el("blockquote", { text: `"${liked.topComment.text}" – ${liked.topComment.name}` }) : null)) });

  // 6. Per month
  const months = Array.from({ length: 12 }, () => 0);
  for (const i of fish) months[new Date(i.ts).getMonth()]++;
  const used = months.map((v, m) => ({ m, v })).filter((x) => x.v);
  if (used.length > 1) {
    const best = used.reduce((a, b) => (b.v > a.v ? b : a));
    const monthName = (m: number, style: "short" | "long") => new Intl.DateTimeFormat(LOCALE, { month: style }).format(new Date(2026, m, 15));
    slides.push({ draw: () => el("div", { class: "slide chart" }, el("h2", { text: t("seasonMonths") }),
      el("div", { class: "months" }, ...months.map((v, m) => el("div", { class: "mcol" + (m === best.m ? " best" : ""), style: `--h:${Math.round(v / best.v * 100)}%;--d:${m * 80}ms` },
        el("span", { class: "mbar" }, v ? el("b", { class: "num", text: String(v) }) : null), el("small", { text: monthName(m, "short") })))),
      el("p", { class: "snote", text: t("seasonBestMonth", monthName(best.m, "long"), best.v) })) });
  }

  // 7. People: who caught most, and each person's biggest
  if (people.length > 1) {
    const rows = people.map((p) => {
      const theirs = fish.filter((i) => i.who === p), big = theirs.filter((i) => i.weightKg).sort(byWeight)[0];
      return { label: p, value: theirs.length, note: theirs.length + (big ? ` · ${big.species ?? ""} ${fmtNum(big.weightKg, 1)} kg` : "") };
    }).sort((a, b) => b.value - a.value).slice(0, 8);
    if (rows[0].value) slides.push({ draw: () => el("div", { class: "slide chart" }, el("h2", { text: t("seasonPeopleTitle") }), bars(rows, rows[0].value)) });
  }

  // 8. Montage of every photo
  if (photos.length) slides.push({ montage: [...photos].sort((a, b) => a.ts - b.ts) });

  // 9. Thanks
  slides.push({ draw: () => el("div", { class: "slide opener end" }, collage(photos.slice(-6)),
    el("div", { class: "stext" }, el("h1", { text: t("seasonThanks") }), el("p", { class: "num", text: t("seasonEndLine", fish.length, photos.length) }))) });
  return slides;
}

/* ---------- player ---------- */
const dlg = $<HTMLDialogElement>("#season-player");
interface Player {
  slides: Slide[];
  i: number;
  paused: boolean;
  timer?: number;
  montageTimer?: number;
  startedAt?: number;
  /** Time left on the current slide when paused, ms. */
  remaining?: number;
  lock?: WakeLockSentinel;
}
let sp: Player | null = null;

function playSeason(title: string, items: Item[]): void {
  if (!items.length) { toast(t("seasonEmpty")); return; }
  const player: Player = { slides: buildSlides(title, items), i: 0, paused: false };
  sp = player;
  $("#sp-bars").replaceChildren(...player.slides.map(() => el("span", {}, el("i"))));
  dlg.showModal();
  navigator.wakeLock?.request("screen").then((l) => { player.lock = l; }).catch(() => {}); // keep the screen on
  showSlide(0);
}
const slideMs = (s: Slide) => ("montage" in s ? Math.max(SLIDE_MS, s.montage.length * MONTAGE_MS) : SLIDE_MS);
function showSlide(i: number): void {
  const p = sp;
  if (!p) return;
  if (i >= p.slides.length) { closeSeason(); return; }
  p.i = Math.max(0, i);
  clearTimeout(p.timer); clearInterval(p.montageTimer);
  const s = p.slides[p.i], stage = $("#sp-stage");
  if ("montage" in s) {
    const img = el("img", { class: "montimg", alt: "" }), cap = el("div", { class: "montcap" });
    stage.replaceChildren(el("div", { class: "slide montage" }, img, cap));
    let k = 0;
    const step = () => {
      const item = s.montage[k % s.montage.length];
      img.src = item.image ?? "";
      img.classList.remove("kb"); void img.offsetWidth; img.classList.add("kb"); // restart the slow zoom
      cap.textContent = [item.who, item.species, dFmtY.format(item.ts)].filter(Boolean).join(" · ");
      k++;
    };
    step();
    p.montageTimer = window.setInterval(() => { if (!p.paused) step(); }, MONTAGE_MS);
  } else stage.replaceChildren(s.draw());
  [...$("#sp-bars").children].forEach((b, n) => {
    b.className = n < p.i ? "done" : n === p.i ? "now" : "";
    (b.firstChild as HTMLElement).style.animationDuration = slideMs(s) + "ms";
  });
  p.remaining = slideMs(s);
  p.startedAt = Date.now();
  if (!p.paused) p.timer = window.setTimeout(() => showSlide(p.i + 1), p.remaining); // stays put while paused
}
function pauseSeason(on: boolean): void {
  const p = sp;
  if (!p || p.paused === on) return;
  p.paused = on;
  dlg.classList.toggle("paused", on);
  if (on) { clearTimeout(p.timer); p.remaining = (p.remaining ?? SLIDE_MS) - (Date.now() - (p.startedAt ?? Date.now())); }
  else { p.startedAt = Date.now(); p.timer = window.setTimeout(() => showSlide(p.i + 1), Math.max(300, p.remaining ?? SLIDE_MS)); }
}
function closeSeason(): void {
  if (sp) { clearTimeout(sp.timer); clearInterval(sp.montageTimer); sp.lock?.release().catch(() => {}); }
  sp = null;
  if (dlg.open) dlg.close();
}
$("#sp-close").addEventListener("click", closeSeason);
dlg.addEventListener("cancel", (e) => { e.preventDefault(); closeSeason(); });
// tap the right two thirds to go on, the left third to go back; hold anywhere to pause
let holdTimer = 0, held = false;
$("#sp-stage").addEventListener("pointerdown", () => { held = false; holdTimer = window.setTimeout(() => { held = true; pauseSeason(true); }, 250); });
$("#sp-stage").addEventListener("pointerup", (e) => {
  clearTimeout(holdTimer);
  if (held) { pauseSeason(false); return; }
  if (!sp) return;
  if (sp.paused) pauseSeason(false);
  showSlide(sp.i + (e.clientX < innerWidth / 3 ? -1 : 1));
});
$("#sp-stage").addEventListener("pointercancel", () => { clearTimeout(holdTimer); pauseSeason(false); });
document.addEventListener("keydown", (e) => {
  if (!sp) return;
  if (e.key === "ArrowRight") showSlide(sp.i + 1);
  if (e.key === "ArrowLeft") showSlide(sp.i - 1);
  if (e.key === " ") pauseSeason(!sp.paused);
});

/* ---------- choosing the season ---------- */
async function openSeasonPicker(title: string, loadItems: () => Promise<Item[]>): Promise<void> {
  const items = await loadItems();
  if (!items.length) { toast(t("seasonEmpty")); return; }
  const options = seasonsIn(items).filter((s) => inSeason(items, s.id).length);
  openSheet(t("seasonPick"), [el("p", { class: "hint", text: t("seasonPickText") }),
    el("div", { class: "chips" }, ...options.map((s) => el("button", { class: "chip", type: "button", onclick: () => { closeSheet(); playSeason(title, inSeason(items, s.id)); } },
      `${s.label} (${inSeason(items, s.id).length})`)))],
    [grow(), cancelButton()]);
}
// The season recap is ReelHunt Plus (a group recap is shown by a member who has Plus).
const needPlus = (go: () => void) => { if (hasPlus()) go(); else openPaywall("plus", { reason: t("payWhySeason"), onDone: go }); };
export function openGroupSeason(): void {
  const g = chatGroup();
  if (g) needPlus(() => void openSeasonPicker(g.name, () => groupSeasonItems(g)));
}
$("#own-season").addEventListener("click", () => needPlus(() => void openSeasonPicker(t("seasonOwnTitle"), ownSeasonItems)));
onRender(() => { const b = $("#own-season"); b.hidden = catches.length < 3; b.dataset.plus = hasPlus() ? "" : "1"; });
